import React from 'react';
import { FormControl, Select, SelectChangeEvent } from '@mui/material';

type PricingTierSelectProps = {
  value: string;
  onChange: (value: string) => void;
  children: React.ReactNode;
};

/**
 * Full-width tier dropdown for pricing cards (Billing V2).
 */
const PricingTierSelect: React.FC<PricingTierSelectProps> = ({
  value,
  onChange,
  children,
}) => (
  <FormControl
    fullWidth
    size="small"
    sx={{
      minWidth: 0,
      width: '100%',
      maxWidth: '100%',
      '& .MuiOutlinedInput-root': {
        borderRadius: 2,
        bgcolor: 'background.paper',
        transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
        '&:hover': {
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        },
        '&.Mui-focused': {
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
        },
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: 'divider',
      },
      '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: 'primary.light',
      },
      '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderWidth: 2,
        borderColor: 'primary.main',
      },
      '& .MuiSelect-select': {
        py: 1.25,
        px: 1.75,
        fontWeight: 500,
        fontSize: '0.875rem',
        lineHeight: 1.4,
        display: 'flex',
        alignItems: 'center',
      },
      '& .MuiSelect-icon': {
        color: 'primary.main',
      },
    }}
  >
    <Select
      value={value}
      variant="outlined"
      onChange={(e: SelectChangeEvent<string>) =>
        onChange(String(e.target.value))
      }
      MenuProps={{
        disableAutoFocusItem: true,
        anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
        transformOrigin: { vertical: 'top', horizontal: 'left' },
        PaperProps: {
          sx: {
            borderRadius: 2,
            mt: 0.5,
            maxHeight: 320,
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
            border: 1,
            borderColor: 'divider',
            '& .MuiMenuItem-root': {
              fontSize: '0.875rem',
              py: 1,
              borderRadius: 1,
              mx: 0.5,
              my: 0.25,
            },
            '& .MuiMenuItem-root.Mui-selected': {
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              '&:hover': {
                bgcolor: 'primary.dark',
              },
            },
          },
        },
      }}
    >
      {children}
    </Select>
  </FormControl>
);

export default PricingTierSelect;
